import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { ANNOUNCEMENTS } from '../config/announcements';
import { track } from '../services/analytics';
import { getSeenAnnouncements, markAnnouncementsSeen } from '../services/api';

// Where seen flags lived when they were kept per browser. They are imported to the
// server on the first sign-in after the change, then removed.
const LEGACY_STORAGE_KEY = 'dentara_seen_announcements';

// "What's new" entries share the seen table under this prefix. They are not tips, so the
// daily tip limit ignores them.
export const WHATS_NEW_PREFIX = 'whats_new:';

// How long a "Show me" request waits for its target to appear on the page
const REQUEST_TIMEOUT_MS = 10000;

const SpotlightContext = createContext();

const isImpersonating = () => localStorage.getItem('authToken') === 'impersonation_session';

const readLegacySeen = () => {
  try {
    const stored = localStorage.getItem(LEGACY_STORAGE_KEY);
    return stored ? Object.keys(JSON.parse(stored)) : [];
  } catch {
    return [];
  }
};

/**
 * True when this person already had a spotlight today, on their own clock.
 * One per day is the limit, so staff are never walked through a chain of them.
 */
const shownToday = (seenMap) => {
  const today = new Date().toDateString();
  return Object.entries(seenMap).some(([id, seenAt]) =>
    !id.startsWith(WHATS_NEW_PREFIX) && new Date(seenAt).toDateString() === today
  );
};

const findTarget = (featureId, registeredTargets) =>
  registeredTargets.get(featureId)
  || document.querySelector(`[data-spotlight="${featureId}"]`);

/**
 * Find the announcement to show whose target is in the DOM, or null.
 * Returns null until seen state has loaded. A "Show me" request wins over everything,
 * because the user asked for it. Otherwise it is the highest priority unseen announcement,
 * and nothing once today's spotlight has shown.
 */
const findNextAnnouncement = (seenMap, registeredTargets, requestedFeatureId) => {
  if (!seenMap) return null;

  if (requestedFeatureId) {
    const requested = ANNOUNCEMENTS.find(a => a.featureId === requestedFeatureId);
    return requested && findTarget(requested.featureId, registeredTargets) ? requested : null;
  }

  if (shownToday(seenMap)) return null;

  const unseen = ANNOUNCEMENTS
    .filter(a => !seenMap[a.id])
    .filter(a => !a.condition || a.condition())
    .sort((a, b) => a.priority - b.priority);

  return unseen.find(a => findTarget(a.featureId, registeredTargets)) || null;
};

export const SpotlightProvider = ({ children }) => {
  // { announcement_id: seen_at } from the server. null while signed out, impersonating,
  // or not loaded yet — nothing shows while it is null.
  const [seenAnnouncements, setSeenAnnouncements] = useState(null);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
  const [targetElement, setTargetElement] = useState(null);
  const [targetRect, setTargetRect] = useState(null);
  // featureId from a "Show me" link in What's new, waiting for its target to appear
  const [requestedFeatureId, setRequestedFeatureId] = useState(null);
  const registeredTargets = useRef(new Map());

  /**
   * Register a target element for a feature
   */
  const registerTarget = useCallback((featureId, element) => {
    if (element) {
      registeredTargets.current.set(featureId, element);
    } else {
      registeredTargets.current.delete(featureId);
    }
  }, []);

  /**
   * Load seen state after sign-in. AppContent calls this.
   */
  const loadSeenAnnouncements = useCallback(async () => {
    // Impersonation is for admin staff. It never shows, records or counts a spotlight.
    if (isImpersonating()) {
      setSeenAnnouncements(null);
      return;
    }

    try {
      let seen = await getSeenAnnouncements();
      const toImport = readLegacySeen().filter(id => !seen[id]);
      if (toImport.length) {
        seen = await markAnnouncementsSeen(toImport);
      }
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      setSeenAnnouncements(seen);
    } catch (error) {
      // A failed load only means no spotlight this session. It must not break the app.
      console.error('Error loading seen announcements:', error);
      setSeenAnnouncements(null);
    }
  }, []);

  /**
   * Forget seen state and hide any spotlight on sign-out. AppContent calls this.
   */
  const clearSeenAnnouncements = useCallback(() => {
    setSeenAnnouncements(null);
    setCurrentAnnouncement(null);
    setTargetElement(null);
    setTargetRect(null);
    setRequestedFeatureId(null);
  }, []);

  /**
   * Find and show the next announcement
   */
  const showNextAnnouncement = useCallback(() => {
    // Don't show if there's already an active announcement
    if (currentAnnouncement) return;

    const next = findNextAnnouncement(seenAnnouncements, registeredTargets.current, requestedFeatureId);
    if (!next) return;

    const target = findTarget(next.featureId, registeredTargets.current);
    setCurrentAnnouncement(next);
    setTargetElement(target);
    setTargetRect(target.getBoundingClientRect());
    setRequestedFeatureId(null);
  }, [currentAnnouncement, seenAnnouncements, requestedFeatureId]);

  /**
   * Show the spotlight for a feature on request ("Show me" in What's new), even if it
   * was seen or today's spotlight already showed. Does nothing if the announcement is
   * not live.
   */
  const requestSpotlight = useCallback((featureId) => {
    if (!ANNOUNCEMENTS.some(a => a.featureId === featureId)) return;
    setCurrentAnnouncement(null);
    setTargetElement(null);
    setTargetRect(null);
    setRequestedFeatureId(featureId);
  }, []);

  /**
   * Dismiss the current announcement. It was recorded as seen when it showed, and the
   * next one waits for another day.
   */
  const dismiss = useCallback(() => {
    if (currentAnnouncement) {
      track('spotlight_dismissed', { announcement_id: currentAnnouncement.id });
      setCurrentAnnouncement(null);
      setTargetElement(null);
      setTargetRect(null);
    }
  }, [currentAnnouncement]);

  /**
   * Check if an announcement or a What's new entry (prefixed id) has been seen
   */
  const hasSeenAnnouncement = useCallback((id) => {
    return !!seenAnnouncements?.[id];
  }, [seenAnnouncements]);

  /**
   * Record What's new entries as seen when the list opens. Does nothing while seen state
   * is not loaded, which includes impersonation.
   */
  const markWhatsNewSeen = useCallback((entryIds) => {
    if (!seenAnnouncements) return;

    const ids = entryIds
      .map(id => WHATS_NEW_PREFIX + id)
      .filter(id => !seenAnnouncements[id])
      .slice(0, 50);
    if (!ids.length) return;

    const now = new Date().toISOString();
    setSeenAnnouncements(prev => ({ ...prev, ...Object.fromEntries(ids.map(id => [id, now])) }));
    markAnnouncementsSeen(ids).catch(error => {
      console.error("Error recording seen What's new entries:", error);
    });
  }, [seenAnnouncements]);

  /**
   * Reset seen state for this page session (for testing). Server records stay.
   */
  const resetAnnouncements = useCallback(() => {
    setSeenAnnouncements({});
    setCurrentAnnouncement(null);
    setTargetElement(null);
    setTargetRect(null);
  }, []);

  // Seen the moment it shows, not when it is dismissed. Ignoring a spotlight is fine,
  // and it does not come back on the next page load or on another computer. A re-shown
  // spotlight keeps its first date, so "Show me" does not use up another day.
  useEffect(() => {
    if (!currentAnnouncement) return;
    const { id } = currentAnnouncement;
    track('spotlight_shown', { announcement_id: id });
    setSeenAnnouncements(prev => (prev?.[id] ? prev : { ...prev, [id]: new Date().toISOString() }));
    markAnnouncementsSeen([id]).catch(error => {
      console.error('Error recording seen announcement:', error);
    });
  }, [currentAnnouncement]);

  // A "Show me" request whose target never appears is dropped, so it cannot pop up later
  useEffect(() => {
    if (!requestedFeatureId) return;
    const timer = setTimeout(() => setRequestedFeatureId(null), REQUEST_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [requestedFeatureId]);

  // Check for announcements once seen state is ready
  useEffect(() => {
    const timer = setTimeout(showNextAnnouncement, 500);
    return () => clearTimeout(timer);
  }, [showNextAnnouncement]);

  // Watch for DOM changes to detect when spotlight targets appear. Stops once today's
  // spotlight has shown, unless a "Show me" request is waiting.
  useEffect(() => {
    if (currentAnnouncement || !seenAnnouncements) return;
    if (shownToday(seenAnnouncements) && !requestedFeatureId) return;

    const observer = new MutationObserver(showNextAnnouncement);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [currentAnnouncement, seenAnnouncements, requestedFeatureId, showNextAnnouncement]);

  // Hide an open spotlight when its target leaves the page, for example after the user
  // clicks through to another page. With no backdrop, that click is allowed.
  useEffect(() => {
    if (!targetElement) return;

    const observer = new MutationObserver(() => {
      if (!targetElement.isConnected) {
        setCurrentAnnouncement(null);
        setTargetElement(null);
        setTargetRect(null);
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [targetElement]);

  // Update target rect on window resize/scroll
  useEffect(() => {
    if (!targetElement) return;

    const updateRect = () => {
      setTargetRect(targetElement.getBoundingClientRect());
    };

    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [targetElement]);

  const value = {
    currentAnnouncement,
    targetElement,
    targetRect,
    dismiss,
    hasSeenAnnouncement,
    resetAnnouncements,
    registerTarget,
    loadSeenAnnouncements,
    clearSeenAnnouncements,
    requestSpotlight,
    markWhatsNewSeen,
    seenLoaded: seenAnnouncements !== null,
    isSpotlightActive: !!currentAnnouncement,
  };

  return (
    <SpotlightContext.Provider value={value}>
      {children}
    </SpotlightContext.Provider>
  );
};

export const useSpotlight = () => {
  const context = useContext(SpotlightContext);
  if (!context) {
    throw new Error('useSpotlight must be used within a SpotlightProvider');
  }
  return context;
};
